import React, { useCallback, useMemo, memo } from "react";
import {
  MapGeometry,
  geometryHasPoints,
  MapView,
  MapViewProps,
  SubjectPropertyIcon,
  useBreakpoint,
} from "@avenue-8/sidekick-ui";
import { PropertyDto } from "@cma/shared/apis/comps/cma/generated";
import styled from "@emotion/styled";
import { CircularProgress, IconButton, Typography } from "@mui/material";
import { createId } from "@paralleldrive/cuid2";
import { MlsListingKey } from "@cma/shared/helpers/listing-mls-key";
import { SwitchGeometryConfirmation } from "./components/SwitchGeometryConfirmation";
import CloseIcon from "@mui/icons-material/Close";
import {
  MapFilter,
  FindListingsSchema,
} from "@cma/chat-widgets/find-listings-search/helpers/property-search-filters-form-helpers";
import ExpandIcon from "@mui/icons-material/Expand";
import { useUIStore } from "@cma/store/ui.store";
import { ResultsLimitInfo } from "./components/ResultsLimitInfo";
import {
  GeoJsonMultiPolygon,
  GeoJsonPolygon,
} from "@shared/apis/sidekick/generated";
import { convertPropertyDtoToMapViewProperty } from "../../helpers/convert-property-dto-to-map-view-property";

interface Props {
  status: "loading" | "idle" | "error" | "success";
  mapFilters: FindListingsSchema["mapFilters"];
  onApplyMapFilters: (data: FindListingsSchema) => void;
  comparableProperties?: PropertyDto[];
  subjectProperty?: PropertyDto;
  defaultCenter?: { lat: number; lng: number };
  selectedCompsComposedIds?: string[];
  onPopoverClick?: (id: MlsListingKey) => void;
  onPropertyCheck?: (id: MlsListingKey, checked: boolean) => void;
  bootstrapURLKeys?: MapViewProps["bootstrapURLKeys"];
  presentationType: string | null;
  onClose?: () => void;
  onExpandClick?: () => void;
  totalItems: number;
  geoJsonPolygon?: GeoJsonPolygon | GeoJsonMultiPolygon | null;
}

const getLatLng = (coords?: { latitude?: number; longitude?: number }) => {
  if (!coords) return;
  const { latitude, longitude } = coords;
  if (latitude && longitude) return { lat: latitude, lng: longitude };
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  &[aria-disabled="true"] {
    opacity: 0.5;
    pointer-events: none;

    * {
      pointer-events: none;
    }
  }
`;

const LoaderContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  top: 0;
  left: 0;
  backdrop-filter: blur(2px);
  pointer-events: none;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 0.5em;
  right: 0.5rem;
  z-index: 1000;
  border-radius: 0.5rem;
  background-color: ${(p) => p.theme.button.outlined.colors.background};
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px; // Same from Google Maps
`;

const SearchMapViewComponent = (props: Props) => {
  const {
    status,
    comparableProperties = [],
    subjectProperty,
    selectedCompsComposedIds,
    mapFilters,
    onApplyMapFilters,
    onPopoverClick,
    onPropertyCheck,
    bootstrapURLKeys,
    presentationType,
    onClose,
    defaultCenter,
    onExpandClick,
    totalItems,
    geoJsonPolygon,
  } = props;
  const [draft, setDraft] = React.useState<MapGeometry>({
    shape: "none",
    key: "draft",
  });
  const [confirmGeometry, setConfirmGeometry] = React.useState<
    MapGeometry | undefined
  >(undefined);
  const confirmDialog = useUIStore((state) => state.chatWidgetConfirmDialog);
  const isTabletDown = useBreakpoint("tablet");
  const enabledDrawingTools: MapViewProps["enabledDrawingTools"] =
    useMemo(() => {
      return {
        polygon: isTabletDown,
        circle: true,
        freehand: !isTabletDown,
      };
    }, [isTabletDown]);

  const geometries = React.useMemo(() => {
    const _geometries: MapGeometry[] = [];

    if (
      geoJsonPolygon &&
      geoJsonPolygon.type === "Polygon" &&
      geoJsonPolygon.coordinates.length > 0
    ) {
      _geometries.push({
        shape: "polygon",
        points: geoJsonPolygon.coordinates[0].map(([lng, lat]) => ({
          lat,
          lng,
        })),
        editable: false,
        key: "readonly-polygon",
      });
      return _geometries;
    }

    if (
      geoJsonPolygon &&
      geoJsonPolygon.type === "MultiPolygon" &&
      geoJsonPolygon.coordinates.length > 0
    ) {
      geoJsonPolygon.coordinates.forEach((polygon, index) => {
        _geometries.push({
          shape: "polygon",
          points: polygon[0].map(([lng, lat]) => ({ lat, lng })),
          editable: false,
          key: `readonly-multi-polygon-${index}`,
        });
      });
      return _geometries;
    }

    if (mapFilters && mapFilters.shape !== "none") {
      _geometries.push({
        ...mapFilters,
        editable: false,
        key: "active-map-filter",
      });
    }
    return _geometries;
  }, [mapFilters, geoJsonPolygon]);

  const handleClearGeometries = useCallback(() => {
    onApplyMapFilters({ mapFilters: { shape: "none" } });
    // appEventEmitter.emit({
    //   eventType: "cma-search-shape-cleared",
    //   presentationType,
    // });
  }, [onApplyMapFilters]);

  const handleGeometryCreate = useCallback(
    (geometry: MapGeometry) => {
      geometry.key = createId();
      let mapFilter: MapFilter | undefined;
      if (geometry.shape === "circle") {
        const { center, radius } = geometry;
        if (center != null && radius != null) {
          mapFilter = { shape: "circle", center, radius };
        }
      } else if (geometry.shape === "polygon") {
        mapFilter = { shape: "polygon", points: geometry.points };
      } else if (geometry.shape === "freehand") {
        mapFilter = { shape: "polygon", points: geometry.points };
      }

      if (mapFilter != null) {
        onApplyMapFilters({ mapFilters: mapFilter });
        // if (mapFilter.shape === "polygon")
        //   appEventEmitter.emit({
        //     eventType: "cma-search-polygon-applied",
        //     presentationType,
        //   });
        // else if (mapFilter.shape === "circle")
        //   appEventEmitter.emit({
        //     eventType: "cma-search-radius-applied",
        //     presentationType,
        //   });
        setDraft({ shape: "none", key: "null" });
      }
    },
    [onApplyMapFilters]
  );

  const handleDraftChanged = useCallback(
    async ({ geometry, action }: { geometry: MapGeometry; action: string }) => {
      if (action === "new-shape") {
        const currentShape:
          | "circle"
          | "polygon"
          | "polyline"
          | "freehand"
          | "none"
          | undefined =
          (draft?.shape !== "none" ? draft.shape : null) || mapFilters?.shape;
        if (
          currentShape != null &&
          currentShape !== "none" &&
          geometry.shape !== currentShape &&
          geometryHasPoints(draft)
        ) {
          setConfirmGeometry(geometry);
          return;
        }
      }
      setDraft(geometry);
    },
    [draft, mapFilters?.shape]
  );

  const properties = React.useMemo((): MapViewProps["properties"] => {
    const result: MapViewProps["properties"] = [];
    comparableProperties?.forEach((cp) => {
      if (
        cp.mlsId === subjectProperty?.mlsId &&
        cp.mlsSource === subjectProperty?.mlsSource
      ) {
        return;
      }

      if (!cp.latitude || !cp.longitude || !cp.mlsId || !cp.mlsSource) return;

      result.push(
        convertPropertyDtoToMapViewProperty({
          propertyDto: cp,
          isSubjectProperty: false,
        })
      );
    });

    if (subjectProperty?.latitude && subjectProperty?.longitude) {
      result.push(
        convertPropertyDtoToMapViewProperty({
          propertyDto: subjectProperty,
          isSubjectProperty: true,
        })
      );
    }

    return result;
  }, [comparableProperties, subjectProperty]);

  const customActions = React.useMemo(() => {
    const actions: MapViewProps["customActions"] = [];
    if (presentationType === "cma") {
      actions.push({
        id: "center-presentation",
        Icon: () => <SubjectPropertyIcon />,
        onClick: (methods) => {
          methods.moveMapTo(methods.center);
          // appEventEmitter.emit({
          //   eventType: "cma-search-center-subject-property-button-clicked",
          //   presentationType,
          // });
        },
      });
    }
    if (onExpandClick) {
      actions.push({
        id: "expand-map-view",
        Icon: () => <ExpandIcon />,
        onClick: () => {
          onExpandClick();
        },
      });
    }
    return actions;
  }, [presentationType, onExpandClick]);

  const handleClose = useCallback(() => {
    if (!onClose) return;
    if (draft.shape !== "none" && geometryHasPoints(draft)) {
      const confirmClose = confirmDialog({
        title: "Unsaved Changes",
        content: "You have unsaved changes. Are you sure you want to close?",
        confirmButtonText: "Yes, Close",
        cancelButtonText: "Cancel",
      });
      if (!confirmClose) return;
    }
    onClose();
  }, [confirmDialog, draft, onClose]);

  const mapKey = useMemo(() => {
    if (!comparableProperties?.length) return "empty";
    return `properties-${comparableProperties.length}-${comparableProperties[0].id}-${comparableProperties[comparableProperties.length - 1].id}`;
  }, [comparableProperties]);

  const resolvedDefaultCenter = useMemo(() => {
    const subjectCoords = getLatLng(subjectProperty);
    return subjectCoords || defaultCenter;
  }, [subjectProperty, defaultCenter]);

  if (status === "error") {
    return (
      <Container>
        <Typography variant="h6" color="error" textAlign="center">
          An error occurred while loading the map. Please try again later.
        </Typography>
      </Container>
    );
  }

  return (
    <Container aria-disabled={status === "loading"}>
      {status === "loading" && (
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      )}
      <SwitchGeometryConfirmation
        currentGeometry={draft}
        selectedGeometry={confirmGeometry}
        onConfirmed={(confirmed) => {
          if (confirmed) {
            setDraft(confirmGeometry!);
          }
          setConfirmGeometry(undefined);
        }}
      />
      {onClose && (
        <CloseButton onClick={handleClose} aria-label="close">
          <CloseIcon />
        </CloseButton>
      )}
      <ResultsLimitInfo totalItems={totalItems} />
      <MapView
        key={mapKey}
        isExternallyHidden={
          presentationType === "general" && properties.length === 0
        }
        // hasGeolocationButton={presentationType === "general"}
        customActions={customActions}
        // onGeolocationButtonClick={() => {
        //   appEventEmitter.emit({
        //     eventType: "cma-search-geolocation-button-clicked",
        //     presentationType,
        //   });
        // }}
        defaultCenter={resolvedDefaultCenter}
        draft={draft}
        onDraftChanged={handleDraftChanged}
        properties={properties}
        checkedPropertiesIds={selectedCompsComposedIds}
        geometries={geometries}
        onClearGeometries={handleClearGeometries}
        onGeometryCreate={handleGeometryCreate}
        onPopoverClick={onPopoverClick}
        onPropertyCheck={onPropertyCheck}
        bootstrapURLKeys={bootstrapURLKeys}
        enabledDrawingTools={enabledDrawingTools}
        mapMarkerDisplayMode="property-price"
        visiblePropertiesBoundsBuffer={{
          lat: 0.5,
          lng: 0,
        }}
      />
    </Container>
  );
};

export const SearchMapView = memo(SearchMapViewComponent);
